import React, { useState, useEffect } from 'react';
import { getDisputeStats } from '../../services/api';
import { extractData } from '../../utils/apiHelpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import DateRangePicker from '../../components/common/DateRangePicker';

const DisputeStats = ({ admin }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date()
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await getDisputeStats();
      const data = extractData(response);
      console.log('Dispute stats data:', data);
      setStats(data);
    } catch (err) {
      Alert.error('Failed to fetch dispute statistics');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'item_not_as_described': 'Item Not as Described',
      'item_not_received': 'Item Not Received',
      'damaged_item': 'Damaged Item',
      'wrong_item': 'Wrong Item',
      'seller_unresponsive': 'Seller Unresponsive',
      'delivery_issue': 'Delivery Issue',
      'quality_issue': 'Quality Issue',
      'other': 'Other',
      'buyer_non_payment': 'Buyer Non-Payment',
      'buyer_unresponsive': 'Buyer Unresponsive',
      'buyer_cancellation': 'Buyer Cancellation',
      'buyer_harassment': 'Buyer Harassment',
      'meeting_no_show': 'Meeting No-Show',
      'fraudulent_claim': 'Fraudulent Claim'
    };
    return categories[category] || category;
  };

  const getResolutionLabel = (resolution) => {
    const resolutions = {
      'refund_full': 'Full Refund',
      'refund_partial': 'Partial Refund',
      'release_funds': 'Release Funds',
      'split_funds': 'Split Funds',
      'cancel_order': 'Cancel Order'
    };
    return resolutions[resolution] || resolution;
  };

  const formatTime = (hours) => {
    if (!hours || hours === 0) return 'N/A';
    if (hours < 24) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  if (loading && !stats) {
    return <LoadingSpinner />;
  }

  const statsData = stats?.stats || {};
  const categories = stats?.categories || [];
  const resolutions = stats?.resolutions || [];
  const recentDisputes = stats?.recent_disputes || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dispute Statistics</h1>
          <p className="text-gray-600">Analytics and performance metrics for dispute resolution</p>
        </div>
        <DateRangePicker
          startDate={dateRange.start}
          endDate={dateRange.end}
          onChange={(start, end) => setDateRange({ start, end })}
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Disputes</p>
              <p className="text-3xl font-bold">{formatNumber(statsData.total_disputes)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-400 bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-2xl">⚖️</span>
            </div>
          </div>
          <p className="text-xs opacity-90 mt-2">
            All time dispute cases
          </p>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Resolved</p>
              <p className="text-3xl font-bold">{formatNumber(statsData.resolved_disputes)}</p>
            </div>
            <div className="w-12 h-12 bg-green-400 bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-2xl">✅</span>
            </div>
          </div>
          <p className="text-xs opacity-90 mt-2">
            Successfully resolved
          </p>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg shadow-lg text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Avg. Resolution</p>
              <p className="text-3xl font-bold">{formatTime(statsData.avg_resolution_hours)}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-400 bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-2xl">⏱️</span>
            </div>
          </div>
          <p className="text-xs opacity-90 mt-2">
            Average resolution time
          </p>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Last 30 Days</p>
              <p className="text-3xl font-bold">{formatNumber(statsData.disputes_last_30_days)}</p>
            </div>
            <div className="w-12 h-12 bg-purple-400 bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-2xl">📅</span>
            </div>
          </div>
          <p className="text-xs opacity-90 mt-2">
            New disputes in last month
          </p>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{formatNumber(statsData.pending_disputes)}</p>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <span className="text-yellow-600">⏳</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Awaiting review</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Review</p>
              <p className="text-2xl font-bold text-orange-600">{formatNumber(statsData.in_review_disputes)}</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600">🔍</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Under investigation</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Closed</p>
              <p className="text-2xl font-bold text-gray-600">{formatNumber(statsData.closed_disputes)}</p>
            </div>
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-gray-600">🔒</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Finalized cases</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Resolution Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {statsData.total_disputes > 0 
                  ? `${Math.round((statsData.resolved_disputes / statsData.total_disputes) * 100)}%` 
                  : '0%'}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600">📈</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Successful resolutions</p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Categories Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-lg mb-4">Disputes by Category</h3>
          <div className="space-y-3">
            {categories.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No category data available</p>
            ) : (
              categories.map((category, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-700">{getCategoryLabel(category.category)}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="font-medium">{category.dispute_count || 0}</span>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-full rounded-full ${
                          category.dispute_count > 20 ? 'bg-red-500' :
                          category.dispute_count > 10 ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }`}
                        style={{ 
                          width: `${Math.min(100, (category.dispute_count || 0) / (statsData.total_disputes || 1) * 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Resolution Types */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-lg mb-4">Resolution Types</h3>
          <div className="space-y-3">
            {resolutions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No resolution data available</p>
            ) : (
              resolutions.map((resolution, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium">{getResolutionLabel(resolution.resolution)}</p>
                    <p className="text-sm text-gray-500">{resolution.resolution}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{resolution.resolution_count || 0}</p>
                    <p className="text-xs text-gray-500">cases</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Disputes */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Recent Disputes</h3>
          <p className="text-sm text-gray-600">Latest dispute cases</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dispute ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parties
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentDisputes.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No recent disputes
                  </td>
                </tr>
              ) : (
                recentDisputes.map((dispute, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="font-medium">#{dispute.dispute_id}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                        {getCategoryLabel(dispute.category)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium">#{dispute.order_id}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium">Buyer: </span>
                          <span>{dispute.raised_by_first_name} {dispute.raised_by_last_name}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Seller: </span>
                          <span>{dispute.raised_against_first_name} {dispute.raised_against_last_name}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {dispute.formatted_date || new Date(dispute.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Charts (Placeholder) */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-lg mb-4">Performance Trends</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <p className="text-gray-500 mb-2">Dispute Volume Over Time</p>
              <p className="text-sm text-gray-400">
                Monthly dispute counts and trends
              </p>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <p className="text-gray-500 mb-2">Resolution Time Analysis</p>
              <p className="text-sm text-gray-400">
                Average resolution times by category
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Analytics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-lg mb-4">Category Analysis</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Count
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % of Total
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg. Resolution Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Most Common Resolution
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categories.map((category, index) => {
                const percentage = statsData.total_disputes > 0 
                  ? ((category.dispute_count || 0) / statsData.total_disputes * 100).toFixed(1)
                  : 0;
                
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium">{getCategoryLabel(category.category)}</span>
                    </td>
                    <td className="px-4 py-3 font-medium">{category.dispute_count || 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <span>{percentage}%</span>
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-full rounded-full bg-blue-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {formatTime(category.avg_hours)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {category.most_common_resolution 
                          ? getResolutionLabel(category.most_common_resolution)
                          : 'N/A'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-lg">Export Data</h3>
            <p className="text-sm text-gray-600">Download dispute statistics and reports</p>
          </div>
          <div className="flex space-x-2">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Export as CSV
            </button>
            <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700">
              Generate Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisputeStats;